import {
  AfterViewInit,
  Component,
  ComponentFactoryResolver,
  Inject,
  OnInit,
  ViewChild,
  ViewContainerRef
} from '@angular/core';
import {SectionBase} from '../../../shared/components/section/section-base';
import {StepValidationStorageService} from '../../../shared/services/step-validation-storage.service';
import {MetricsService} from '../../../shared/services/metrics.service';
import {CustomMetricsName} from '../../../shared/components/models/metrics/custom-metrics-name.enum';
import {MetricsType} from '../../../shared/components/models/metrics/metrics-type.enum';
import {isNotNullOrUndefined} from 'codelyzer/util/isNotNullOrUndefined';
import {DeviceService} from '../../../shared/services/device.service';
import {SectionModeState, SectionsStateService} from '../../../shared/services/sections-state.service';
import {ApplicationStateService} from '../../../shared/services/application-state.service';
import {noop} from '../../utils/utils';
import {OlxAnalyticsService} from '../../../shared/services/olx-analytics.service';
import {SalesChannel} from '../../consts/sales-channel.enum';
import {StepKey} from '../../enums/step-key.enum';
import {ResourceService} from '../../../shared/services/resource.service';
import {GoogleAnalyticsEventsService} from '../../../shared/services/google-analytics-events.service';
import {GA4EventKey} from '../../consts/ga4-event-key.enum';
import {ScrollService} from '../../../shared/services/scroll.service';
import {InternalStepsConfigService} from '../../services/impl/configuration/internal-steps-config.service';
import {StepConfig} from '../../configuration/step-config';
import {ComponentsFactoryService} from '../../../shared/services/components-factory.service';
import {CalculationService} from '../../services/impl/calculation.service';

@Component({
  selector: 'app-wizard-step',
  templateUrl: './wizard-step.component.html',
  styleUrls: ['./wizard-step.component.scss']
})
export class WizardStepComponent implements OnInit, AfterViewInit {
  
  @ViewChild('sectionsContainer', {read: ViewContainerRef}) sectionsContainer;
  
  title: string;
  mobile: boolean;
  stepConfig: StepConfig;
  sectionsConfig: any[];
  stepKey: string;
  substepIndex: number;
  
  private _sectionsBase: SectionBase[] = [];
  private sectionsErrorsLogEnabled: boolean = false;

  get isOLX(): boolean {
    return this.appStateService.salesChannel === SalesChannel.OLX;
  }
  
  constructor(@Inject(ComponentFactoryResolver) private _factoryResolver: ComponentFactoryResolver,
              private metricsService: MetricsService,
              private stateSectionService: SectionsStateService,
              private appStateService: ApplicationStateService,
              private olxAnalyticsService: OlxAnalyticsService,
              private _stepValidationStorageService: StepValidationStorageService,
              private resourceService: ResourceService,
              private deviceService: DeviceService,
              private internalStepsConfigService: InternalStepsConfigService,
              private scrollService: ScrollService,
              private googleAnalyticsService: GoogleAnalyticsEventsService,
              private componentsFactoryService: ComponentsFactoryService,
              private calculationService: CalculationService) {
  }
  
  ngOnInit() {
  }

  ngAfterViewInit(): void {
    this.updateScrollPosition();
    setTimeout(() => this.resourceService.updatePdfLinks(), 0);
    this.updateRealTimeStepValidity();
    this.googleAnalyticsService.send(GA4EventKey.STEP_INIT);
  }

  init() {
    this.sendStepInitMetrics();
    this.sectionsConfig.forEach(section => {
      const component = this.componentsFactoryService.getComponent(section.key);
      this.createSectionState(section);
      if (component) {
        if (section.hasOwnProperty('mobile')) {
          if (isNotNullOrUndefined(section.mobile) && this.deviceService.isMobileDevice === section.mobile) {
            this.createComponentInstance(component, section);
          }
        } else {
          this.createComponentInstance(component, section);
        }
      } else {
        console.warn('No instance found for component key:', section.key);
      }
    });

    this._sectionsBase.forEach(section => section.sectionFieldChange.subscribe(() => {
      setTimeout(() => this.updateRealTimeStepValidity(), 0);
    }));
  }
  
  submit() {
    this._sectionsBase = this._sectionsBase.filter(section => !section.hasOwnProperty('sectionVisible') || section['sectionVisible'])
    this._sectionsBase.forEach(section => {
      section.submitSection();
    });
    const isStepValid = this.isValidStep();
    this._sectionsBase.forEach(section => {
      section.onStepSubmitted(isStepValid);
      if (this.sectionsErrorsLogEnabled && isNotNullOrUndefined(section.formFactoryService)) {
        console.log(section.key, 'section errors', section.formFactoryService.errorsMap);
      }
    });
    const formsErrors = this._sectionsBase
        .filter(sectionBase => isNotNullOrUndefined(sectionBase.formFactoryService))
        .map(sectionBase => ({
          sectionKey: sectionBase.key,
          errors: Array.from(sectionBase.formFactoryService.errorsMap)
        }));
    const shouldLogErrors = formsErrors.some(sectionErrors => isNotNullOrUndefined(sectionErrors.errors) && sectionErrors.errors.length > 0);
    if (shouldLogErrors) {
      this.metricsService.sendSimpleMetric('formErrors', MetricsType.PROCESS, JSON.stringify(formsErrors));
      this.metricsService.sendSimpleMetric(CustomMetricsName.CALCULATION_DATA, MetricsType.COLLECTED_DATA, JSON.stringify(this.calculationService.calculationData));
    }
  }
  
  isValidStep(): boolean {
    let isValid = this._sectionsBase.filter(section => {
      return !section.valid
    }).length === 0;
    this._stepValidationStorageService.updateStepValidationStorage(this.stepKey, isValid, this.substepIndex);
    return isValid;
  }
  
  updatePreviewSectionState(): void {
    this.appStateService.state.usedPreviewMode ?
      this._sectionsBase.filter(section => {
        this.stateSectionService.createOrUpdateSectionState(section.key, SectionModeState.PREVIEW);
      }) : noop();
  }
  
  errorsOnStep(): boolean {
    let areErrors = this._sectionsBase.filter(section => section.areErrors()).length !== 0;
    return areErrors;
  }

  handleScrollBehaviour(scrollToFirstSectionAllowed: boolean): void {
    if (scrollToFirstSectionAllowed && this.internalStepsConfigService.shouldScrollToFirstSection(this.stepKey)) {
      this.scrollToFirstSection();
    }
  }

  private updateScrollPosition(): void {
    if (this.internalStepsConfigService.shouldScrollToTop(this.stepKey)) {
      this.scrollService.scrollToTop();
      return;
    }
    if (this.internalStepsConfigService.shouldScrollToFirstSection(this.stepKey)) {
      this.scrollService.restoreScrollPositionAfterStepChange();
    }
  }

  private scrollToFirstSection(): void {
    const firstSection = this._sectionsBase.find(sectionBase => sectionBase.key === this.stepConfig.sections[0].key);
    if (isNotNullOrUndefined(firstSection) && firstSection.sectionClass) {
      this.scrollService.scrollToView(`.${firstSection.sectionClass}`, 300);
    }
  }
  
  private createSectionState(section) {
    this.stateSectionService.createOrUpdateSectionState(section.key,
      this.appStateService.state.usedPreviewMode
        ? this.stateSectionService.sectionState(section.key) && this.stateSectionService.sectionState(section.key).mode
          ? this.stateSectionService.sectionState(section.key).mode
          : SectionModeState.PREVIEW
        : SectionModeState.EDIT
    );
  }
  
  private sendStepInitMetrics(): void {
    this.metricsService.sendSimpleMetric(CustomMetricsName.STEP_INIT, MetricsType.COMPONENT, this.stepKey);
    if (this.isOLX) {
      this.olxAnalyticsService.sendStepEvent(<StepKey>this.stepKey);
    }
  }
  
  private createComponentInstance(component, section) {
    const factory = this._factoryResolver.resolveComponentFactory(component);
    const componentRef = this.sectionsContainer.createComponent(factory);
    componentRef.instance.title = section.title ? section.title : null;
    componentRef.instance.mobile = this.mobile;
    componentRef.instance.key = section.key;
    this._sectionsBase.push(componentRef.instance);
  }

  private updateRealTimeStepValidity(): void {
    let isValid = this._sectionsBase.filter(section => {
      return isNotNullOrUndefined(section.valid) && !section.valid
    }).length === 0;
    this._stepValidationStorageService.updateRealTimeStepValidity(this.stepKey, isValid, this.substepIndex);
  }
  
}
